import { useState, useEffect, useContext, useRef } from "react";
import TopMenu from "./TopNav";
import SideNav from "./SideNav";
import GlobalModal from "../modals/GlobalModal/GlobalModal";
import { useHistory, useParams } from "react-router-dom";
import OrganisationService from "service/organisationService";
import LocalStorageService from "service/localStorageService";
import AccountService from "service/accountService";
import {
    getLatestSubscription,
    getSubscriptionById,
    getWalletStatus,
    getAllTokens,
} from "service/subscriptionService";
import { isHttpSuccess } from "utils/functions";
import {
    changeOrganizationAction,
    storeOrganizationAction,
    storeUserAction,
    storeOgrActiveTabAction,
    verifyOrgOwnerAction,
    updateModalDataAction,
    storePanelsAction,
    storeTokenBalanceAction,
    updateSubscriptionStatus,
    storeSubscriptionID,
    storeOrgMembersAction,
    storeOrgGroupsAction,
    storeOrgRolesAction,
} from "store/actions";
import {
    EventKey,
    SubscriptionStatus,
    FETCH_ORGANIZATION_FAIL_MESSAGE,
    HttpStatus,
} from "constant";
import { AppContext } from "context/appContext";
import useOrganizationVanilla, {
    changeToOrganization,
} from "hooks/organization";
import OrganizationWrapperModal from "../modals/OrganizationWrapperModal";
import { useWalletStore } from "hooks/wallet";
import { usePermissions } from "hooks/common";
import { OrganisationDto } from "types";
import { getSubscriptionPlanType } from "components/subscriptions/CurrentPlanInformation";
import { getAPIError, showErrorAlert } from "utils/alert";
import { useDetectDateTimeFormat } from "hooks/useDetectDateTimeFormat";
import { useAuth } from "hooks/useAuth";
const Layout = (props: any) => {
    const [sideMenu, setSideMenu] = useState(false);
    const updateSideMenu = () => {
        setSideMenu(!sideMenu);
    };
    const history = useHistory();
    const params: any = useParams();
    const { signOut } = useAuth();
    const [orgLoaded, setOrgLoaded] = useState(false);
    const { storeData, storeDispatchActions } = useContext(AppContext);
    const { organization, user, globalModal, subscription } = storeData;
    const [subscriptionType, setSubscriptionType] = useState<any>("");

    usePermissions();

    // Detect datetime format changes on route changes
    useDetectDateTimeFormat();

    const currentWallet = useWalletStore.getState().wallet;

    const isMountedRef = useRef(true);

    // Add this useEffect to manage mount status
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        const email = user.userData?.email;
        if (email && !orgLoaded && !organization.isUserOrgLoaded) {
            OrganisationService.getUserOrgs().then((res) => {
                if (!isMountedRef.current) {
                    return;
                }

                if (res.status === 200) {
                    const { owner, member } = res.data;

                    let currentOrg;
                    let orgList = [...owner, ...member].toCamelCase();

                    if (params.orgId) {
                        currentOrg = orgList.find(
                            (org: any) => org.uuid === params.orgId
                        );
                    }

                    if (!currentOrg) {
                        const orgId =
                            // get orgId from lastLoggedOrgId first to make it align with authenticate.ts logic
                            // dont use ??, LocalStorageService.getItem return empty string as default
                            LocalStorageService.getItem("lastLoggedOrgId") ||
                            LocalStorageService.getItem("orgId");
                        currentOrg = orgList.find(
                            (org: any) => org.uuid === orgId
                        );

                        // If can not find anything will get from api response, set the default one
                        if (!currentOrg) {
                            currentOrg = orgList.find((org: OrganisationDto) => org.isDefault);
                        }
                    }

                    LocalStorageService.setItem("orgIdList", orgList);
                    LocalStorageService.setItem("orgId", currentOrg.uuid);

                    const lastLoggedOrgId =
                        LocalStorageService.getItem("lastLoggedOrgId");
                    if (lastLoggedOrgId !== currentOrg.uuid) {
                        const isCurrentUserOrg = orgList.some(
                            (org: any) => org.uuid === lastLoggedOrgId
                        );
                        if (isCurrentUserOrg) {
                            // Current user's org, update lastLoggedOrgId
                            LocalStorageService.setItem(
                                "lastLoggedOrgId",
                                currentOrg.uuid
                            );
                        } else {
                            // Different user org, remove lastLoggedOrgId
                            LocalStorageService.removeItem("lastLoggedOrgId");
                        }
                        globalThis.window.loadAuthorizationConfig();
                    }

                    setOrgLoaded(true);
                    storeDispatchActions(storeOrganizationAction({ orgList }));
                    storeDispatchActions(
                        changeOrganizationAction({
                            orgInfo: currentOrg,
                        })
                    );

                    storeDispatchActions(
                        verifyOrgOwnerAction({
                            isOwner: email === currentOrg.owner,
                        })
                    );

                    storeDispatchActions(
                        storeOrgMembersAction({
                            orgMembers: currentOrg.members,
                        })
                    );

                    storeDispatchActions(
                        storeOrgGroupsAction({
                            orgGroups: currentOrg.groups,
                        })
                    );

                    storeDispatchActions(
                        storeOrgRolesAction({
                            orgRoles: currentOrg.roles,
                        })
                    );
                } else {
                    showErrorAlert(
                        getAPIError(res, FETCH_ORGANIZATION_FAIL_MESSAGE)
                    );
                }
            });
        }
    }, [
        orgLoaded,
        storeDispatchActions,
        user.userData?.email,
        params.orgId,
        organization.currentOrgId,
        organization.isUserOrgLoaded,
    ]);

    useEffect(() => {
        !user.isLoaded &&
            AccountService.getAccountInfo().then((response: any) => {
                if (!isMountedRef.current) {
                    return;
                }

                if (response.status === 200) {
                    storeDispatchActions(
                        storeUserAction({
                            userData: response.data as object,
                            isLoaded: true,
                        })
                    );
                } else if (response.status === HttpStatus.FORBIDDEN) {
                    // This is to cover the blank page issue
                    // Its probably happen when user is not authenticated in app backend
                    signOut();
                }
            });
    }, [user.isLoaded, storeDispatchActions, history, signOut]);

    useEffect(() => {
        if (!subscription.isLoaded) {
            getAllTokens().then((total: any) => {
                if (!isMountedRef.current) {
                    return;
                }

                storeDispatchActions(
                    storeTokenBalanceAction({
                        tokenBalance: total,
                        isLoaded: true,
                    })
                );
            });
        }
    }, [subscription.isLoaded, storeDispatchActions, subscription]);

    useEffect(() => {
        if (!subscription.currentSubscriptionId) return;
        (async () => {
            const currentSubscription: any = await getSubscriptionById(
                subscription.currentSubscriptionId
            );
            if (!isMountedRef.current) {
                return;
            }

            if (isHttpSuccess(currentSubscription.status)) {
                const { customer } = currentSubscription.data;

                const { is_prepaid, is_cancellable } = customer ?? {};

                setSubscriptionType(
                    getSubscriptionPlanType({
                        is_prepaid,
                        is_cancellable,
                    })
                );
            }
        })();
    }, [subscription.currentSubscriptionId]);

    useEffect(() => {
        getLatestSubscription()
            .then((latestSubscription: any) => {
                if (!isMountedRef.current) {
                    return;
                }

                if (
                    [
                        SubscriptionStatus.ACTIVE,
                        SubscriptionStatus.CANCELED,
                        SubscriptionStatus.PENDING,
                    ].includes(latestSubscription.status)
                ) {
                    storeDispatchActions(
                        updateSubscriptionStatus({
                            isSubscribed: true,
                            hasSubscribed: true,
                        })
                    );
                    storeDispatchActions(
                        storeSubscriptionID(latestSubscription.uuid)
                    );
                } else {
                    storeDispatchActions(
                        updateSubscriptionStatus({
                            isSubscribed: false,
                            hasSubscribed: true,
                        })
                    );
                }
            })
            .catch((err: any) => {
                if (!isMountedRef.current) {
                    return;
                }

                storeDispatchActions(
                    updateSubscriptionStatus({
                        isSubscribed: false,
                        hasSubscribed: false,
                    })
                );
            });
    }, [storeDispatchActions]);

    useEffect(() => {
        if (organization.currentOrgId) {
            getWalletStatus(organization.currentOrgId);
        }
    }, [organization.currentOrgId, currentWallet]);

    const handleChangeOrg = (
        currentOrg: OrganisationDto,
        isRedirect = true
    ) => {
        if (organization.currentOrgId === currentOrg.uuid) return;

        LocalStorageService.setItem("orgId", currentOrg.uuid);
        globalThis.window.loadAuthorizationConfig().then(() => {
            if (!isMountedRef.current) {
                return;
            }

            switchTopNavUser(currentOrg);
            useOrganizationVanilla.setState({ orgId: currentOrg.uuid });
            changeToOrganization.setState({
                toOrganization: undefined,
            });

            isRedirect && history.push("/dashboard");
        });
    };

    useEffect(() => {
        const unsubscribe = useOrganizationVanilla.subscribe((state) => {
            if (state.isChangeOrganization === true && state.orgId) {
                state.setIsChangeOrganization(undefined);
                const organization =
                    state.organizationList.find(
                        (org) => org.uuid === state.orgId
                    ) || {};
                organization && handleChangeOrg(organization, false);
            }
        });

        return () => {
            unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const switchTopNavUser = (organization: OrganisationDto) => {
        const { userData } = user;
        storeDispatchActions(
            changeOrganizationAction({
                orgInfo: organization,
            })
        );

        storeDispatchActions(
            verifyOrgOwnerAction({
                isOwner: userData.email === organization.owner,
            })
        );

        storeDispatchActions(
            storeOgrActiveTabAction({
                tab: EventKey.members,
            })
        );

        storeDispatchActions(storePanelsAction({ panels: [] }));
    };

    const handleCloseModal = () => {
        if (globalModal.redirectTo) history.push(globalModal.redirectTo);

        storeDispatchActions(
            updateModalDataAction({
                isShow: false,
                mainMessage: "",
                subMessage: [],
            })
        );
    };

    return (
        <div>
            <TopMenu
                logout={signOut}
                sideMenu={sideMenu}
                updateSideMenu={updateSideMenu}
                userInfo={user.userData}
                orgList={organization.orgList}
                currentOrgId={organization.currentOrgId}
                currentOrgInfo={organization.currentOrgInfo}
                onOrgChange={handleChangeOrg}
                subscription={subscription}
                subscriptionType={subscriptionType}
            />

            <SideNav sideMenu={sideMenu} updateSideMenu={updateSideMenu}>
                <OrganizationWrapperModal>
                    {props.children}
                </OrganizationWrapperModal>
            </SideNav>

            <GlobalModal
                isShow={globalModal.isShow}
                mainMessage={globalModal.mainMessage}
                subMessage={globalModal.subMessage}
                onClose={() => handleCloseModal()}
            />
        </div>
    );
};

export default Layout;
